import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ErrorMessageDialog } from '../controllers/error-message-dialog.controller';
import { ErrorLoggingService } from '../services/error-logging.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private errorLogging: ErrorLoggingService,
    private errorMessageDialog: ErrorMessageDialog,
    private ngZone: NgZone,
    private injector: Injector
  ) {}

  async handleError(error: any) {
    if (!error) {
      return;
    }

    const findInnerError = (err: any): any => {
      if (!err || !(err instanceof Error)) {
        return undefined;
      }

      return findInnerError(err.cause) || err.cause;
    };

    const innerError = findInnerError(error);

    const errorMessage =
      error.error?.message ??
      error.rejection?.error?.message ??
      (innerError ?? error).message;

    if (typeof errorMessage !== 'string') {
      return;
    }

    // Error messages indicating maintenance mode
    const maintenanceIndicators = ['could not connect to redis Instance at'];

    // List of errors to ignore
    const ignoreErrors = [
      'ExpressionChangedAfterItHasBeenCheckedError', // Angular change detection errors
      'Member not found. (0x80020003)', // COM object errors from external comms found using WebView2 in Revit
    ];

    // Check if error should be ignored
    if (
      ignoreErrors.some((ie) =>
        errorMessage.toLowerCase().includes(ie.toLowerCase())
      )
    ) {
      if (environment.production === false) {
        console.warn(
          `[GlobalErrorHandler] handleError() - Ignored error: %o`,
          error
        );
      }

      return;
    }

    // Check for maintenance mode indicators
    if (
      maintenanceIndicators.some((indicator) =>
        errorMessage.toLowerCase().includes(indicator.toLowerCase())
      )
    ) {
      // Site under maintenance. Navigate to maintenance page.
      this.ngZone.run(() => {
        const router = this.injector.get(Router);

        router.navigate(['/maintenance'], { replaceUrl: true });
      });

      return;
    }

    // Log error and get error ID
    const errorId = await this.errorLogging.logError(error);

    if (!!errorId && errorId !== '') {
      // Display error dialog
      this.ngZone.run(() => {
        this.errorMessageDialog.show(errorId, error);
      });
    }
  }
}
